import { useFindCrItems } from '@/components/CrItemsSelect/Provider';
import { useFindCrSubItems } from '@/components/CrSubItemsSelect/Provider';
import { useFindExcise } from '@/components/ExciseSelect/Provider';
import { useFindExItems } from '@/components/ExItemSelect/Provider';
import { useGetDeptsWithAgentApplicantResource } from '@/context/services/dept/Dept.service';
import { useGetExItemsForDeptIds } from '@/context/services/ex_item/ExItem.service';
import { useGetProjectCodesWithAgentApplicantResource } from '@/context/services/project_code/ProjectCode.service';
import { INVOICE_NUMBER_UNSAVED } from '@/context/services/reportsType/invoiceReports/type';
import { useFormValue } from '@/features/InvoiceReport/Edit/components/Context';
import { useVisibleTransactionProperties } from '@/features/InvoiceReport/Edit/components/hooks/useVisibleTransactionProperties';
import { useFloorOnAbsolute } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks';
import { ConvertInvoiceTransaction } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/model';
import { mapApiCustomTransactionItemsToFormValues } from '@/features/InvoiceReport/Edit/components/utils/customTransactionItems';
import {
  InvoiceTransaction,
  PaymentRequestForm,
} from '@/features/InvoiceReport/Edit/type';
import { BatchPulldownOptionData } from '@/hooks/useGetCustomTransactionPulldownLabels';
import { useTranslation } from '@/i18n';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { randomId, useToastAPI } from '@moneyforward/ap-frontend-components';
import {
  ApPayeeDetail as ApPayeeModel,
  ExItem,
  InvoiceTransactionDetail,
} from 'ap-openapi';
import Big from 'big.js';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { stringToDayjs } from 'date-util';

export type UseApplyPastTransactionsOptions = {
  canDisplayTransactionDetail?: boolean;
};

export const useApplyPastTransactions = (
  onImport: (items: InvoiceTransaction[]) => void,
  opts?: UseApplyPastTransactionsOptions
) => {
  const { canDisplayTransactionDetail = true } = opts ?? {};
  const isUtilizePastDetailsEnhancement = useGetFlags(
    'UTILIZE_PAST_DETAILS_ENHANCEMENT'
  );
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { isLoading: isLoadingDepts, items: depts } =
    useGetDeptsWithAgentApplicantResource({
      queryKey: ['depts', 'apply-past-transactions'],
    });
  const { findCrItemsById } = useFindCrItems();
  const { findExItemsById } = useFindExItems();
  const { findCrSubItemsById } = useFindCrSubItems();
  const { findExciseById } = useFindExcise();
  const { isLoading: isLoadingProjectCodes, items: projectCodes } =
    useGetProjectCodesWithAgentApplicantResource({
      queryKey: ['project-codes', 'apply-past-transactions'],
    });
  const apiData = useFormValue();

  const canDisplayDrExcise = useMemo(() => {
    return !apiData?.duty_free;
  }, [apiData]);

  const { trigger } = useFormContext<PaymentRequestForm>();
  const floorOnAbsolute = useFloorOnAbsolute();

  const visibleTransactionProperties = useVisibleTransactionProperties({
    canDisplayTransactionDetail,
    canDisplayDrExcise,
  });

  const visibleCustomPropertyIds = useMemo(
    () =>
      new Set(
        visibleTransactionProperties
          .filter((p) => p.type === 'custom')
          .map((p) => p.id)
      ),
    [visibleTransactionProperties]
  );

  // Use the centralized service for fetching ex items
  const { getExItemsForDeptIds, isLoading: isLoadingExItems } =
    useGetExItemsForDeptIds();

  const getDeptIdsExisted = useCallback(
    (transactions: InvoiceTransactionDetail[]): string[] => {
      const deptIds = transactions
        .filter((tran) => !!depts.find((dept) => dept.id === tran.dept?.id))
        .map((tran) => tran.dept?.id)
        .filter((id): id is string => typeof id === 'string');
      return [...new Set(deptIds)];
    },
    [depts]
  );

  const mappingInvoiceTransactions = useCallback(
    async (
      transactions: InvoiceTransactionDetail[],
      selectedPayee?: ApPayeeModel,
      pulldownOptionMap?: BatchPulldownOptionData
    ) => {
      const deptIds = getDeptIdsExisted(transactions);
      const exItemsByDept = await getExItemsForDeptIds(deptIds);

      // Now process all transactions with the fetched data
      return transactions.map((item): InvoiceTransaction => {
        const taxIncludedTotal = Big(item?.total_value || 0).plus(
          item?.excise_value || 0
        );
        const quantity = Big(item?.quantity || 1);
        const dept = depts.find((dept) => dept.id === item.dept?.id);
        const crItem = findCrItemsById(item.cr_item?.id);
        const crSubItem = findCrSubItemsById(item.cr_sub_item?.id);
        const projectCode = projectCodes.find(
          (projectCode) => projectCode.id === item.project_code?.id
        );

        // Validate exItem using the fetched department-specific data
        const deptExItems: ExItem[] = item.dept?.id
          ? exItemsByDept[item.dept.id] || []
          : [];
        const exItemExisted = item.dept?.id
          ? deptExItems.some((ex) => ex.id === item.ex_item?.id)
          : findExItemsById(item.ex_item?.id);

        const hasWithholdingIncomeTax = selectedPayee?.is_withholding_tax
          ? item.has_withholding_income_tax
          : undefined;
        const withholdingIncomeTax = selectedPayee?.is_withholding_tax
          ? item.withholding_income_tax_value
          : undefined;

        const drExciseId = canDisplayDrExcise
          ? findExciseById(item.dr_excise?.id)
            ? item.dr_excise?.id
            : undefined
          : undefined;

        const drExciseName = canDisplayDrExcise
          ? findExciseById(item.dr_excise?.id)
            ? item.dr_excise?.name
            : undefined
          : undefined;

        const exciseValue = canDisplayDrExcise
          ? canDisplayDrExcise
            ? findExciseById(item.dr_excise?.id)
              ? item.excise_value
              : undefined
            : undefined
          : undefined;

        const allCustomTransactionItems =
          mapApiCustomTransactionItemsToFormValues(
            item.custom_transaction_items,
            visibleTransactionProperties,
            pulldownOptionMap
          );
        const customTransactionItems = allCustomTransactionItems
          ? Object.fromEntries(
              Object.entries(allCustomTransactionItems).filter(([key]) =>
                visibleCustomPropertyIds.has(key)
              )
            )
          : undefined;

        return ConvertInvoiceTransaction({
          id: '',
          number: INVOICE_NUMBER_UNSAVED,
          dealDate: isUtilizePastDetailsEnhancement
            ? undefined
            : item.deal_date
            ? stringToDayjs(item.deal_date)
            : undefined,
          name: item.name,
          exItemId: exItemExisted ? item.ex_item?.id : undefined,
          exItemName: exItemExisted ? item.ex_item?.name : undefined,
          unit: item.unit_value,
          quantity: item.quantity,
          totalValue: item.total_value,
          drExciseId: drExciseId,
          drExciseName: drExciseName,
          exciseValue: exciseValue,
          hasWithholdingIncomeTax: hasWithholdingIncomeTax,
          withholdingIncomeTax: withholdingIncomeTax,
          memo: item.memo,
          deptId: dept ? item.dept?.id : undefined,
          deptName: dept ? item.dept?.name : undefined,
          projectCodeId: projectCode ? item.project_code?.id : undefined,
          projectCodeName: projectCode ? item.project_code?.name : undefined,
          currency: item.currency ?? 'JPY',
          useCustomJPYRate: item.use_custom_jpy_rate,
          jpyRate: item.jpyrate ?? 1,
          crItemId: crItem ? item.cr_item?.id : undefined,
          crItemName: crItem ? item.cr_item?.name : undefined,
          crSubItemId: crSubItem ? item.cr_sub_item?.id : undefined,
          crSubItemName: crSubItem ? item.cr_sub_item?.name : undefined,
          taxIncludedTotalValue: floorOnAbsolute(taxIncludedTotal.toNumber()),
          taxIncludedUnit: floorOnAbsolute(
            taxIncludedTotal.div(quantity).toNumber()
          ),
          rowKey: randomId('invoice-transaction', 8),
          ...(customTransactionItems &&
            Object.keys(customTransactionItems).length > 0 && {
              custom_transaction_items: customTransactionItems,
            }),
        });
      });
    },
    [
      floorOnAbsolute,
      depts,
      findCrItemsById,
      findCrSubItemsById,
      projectCodes,
      canDisplayDrExcise,
      getExItemsForDeptIds,
      getDeptIdsExisted,
      findExItemsById,
      findExciseById,
      visibleTransactionProperties,
      visibleCustomPropertyIds,
      isUtilizePastDetailsEnhancement,
    ]
  );

  const handleApplyPastTransactions = useCallback(
    async (
      selectedPayee?: ApPayeeModel,
      transactions?: InvoiceTransactionDetail[],
      onSuccess?: () => void,
      pulldownOptionMap?: BatchPulldownOptionData
    ) => {
      if (!transactions) return;

      const invoiceTransactions = await mappingInvoiceTransactions(
        transactions,
        selectedPayee,
        pulldownOptionMap
      );

      onImport(invoiceTransactions);
      trigger('invoiceTransactions');

      notify.success({
        title: t('payment_past_details_apply_past_transactions_success'),
        duration: 5,
      });
      onSuccess?.();
    },
    [mappingInvoiceTransactions, notify, t, onImport, trigger]
  );

  return {
    handleApplyPastTransactions,
    isLoading: isLoadingExItems || isLoadingDepts || isLoadingProjectCodes,
  };
};
